import { Injectable, signal, effect } from '@angular/core';

const STORAGE_KEY = 'cmt-font-scale';
const STEPS = [0.85, 0.9, 0.95, 1.0, 1.05, 1.1, 1.15, 1.2] as const;
const DEFAULT_SCALE = 1.0;
const BASE_FONT_SIZE = 14; // px

@Injectable({ providedIn: 'root' })
export class FontScaleService {
  /** Current scale factor (0.85 – 1.2) */
  readonly scale = signal(this.loadFromStorage());

  /** Human-readable percentage */
  readonly scalePercent = () => Math.round(this.scale() * 100);

  readonly canDecrease = () => this.scale() > STEPS[0];
  readonly canIncrease = () => this.scale() < STEPS[STEPS.length - 1];

  constructor() {
    // Apply scale to <html> font-size whenever it changes
    effect(() => {
      const px = BASE_FONT_SIZE * this.scale();
      document.documentElement.style.fontSize = `${px}px`;
      localStorage.setItem(STORAGE_KEY, String(this.scale()));
    });
  }

  increase(): void {
    const idx = STEPS.indexOf(this.findClosest(this.scale()));
    if (idx < STEPS.length - 1) this.scale.set(STEPS[idx + 1]);
  }

  decrease(): void {
    const idx = STEPS.indexOf(this.findClosest(this.scale()));
    if (idx > 0) this.scale.set(STEPS[idx - 1]);
  }

  reset(): void {
    this.scale.set(DEFAULT_SCALE);
  }

  private loadFromStorage(): number {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const val = parseFloat(stored);
        if (!isNaN(val) && val >= STEPS[0] && val <= STEPS[STEPS.length - 1]) return val;
      }
    } catch { /* localStorage unavailable */ }
    return DEFAULT_SCALE;
  }

  private findClosest(val: number): typeof STEPS[number] {
    return STEPS.reduce((prev, curr) =>
      Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev,
    );
  }
}
